import React, { useContext, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Axios from "axios";
import { Store } from "../Store";
import { toast } from "react-toastify";
import { getError } from "../utils";
import { api } from "../Api";
import { useOtpGate, OtpGateModal } from "../component/OtpGate";
import SecurityBadges from "../component/SecurityBadges";

export default function TransferScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const [RegisterPoint, setRegisterPoint] = useState(0);
  // Transfers move Register Point only — the server refuses any other source.
  const [convertFrom, setConvertFrom] = useState("point");
  const [convertTo, setConvertTo] = useState(0);
  const [amount, setAmount] = useState(0);
  const [password, setPassword] = useState("");
  const [loading, setloading] = useState(0);
  const [minimumTransfer, setMinimumTransfer] = useState();
  // Money Controls, served alongside the balances. Defaults keep the screen
  // usable if an older backend does not send the block at all.
  const [policy, setPolicy] = useState({
    transferEnabled: true,
    transferDisabledMessage: "",
    minimumTransfer: 0,
    maximumTransfer: 0,
    transferCharge: 0,
  });
  const transferCharge = Number(policy.transferCharge) || 0;
  const enteredAmount = Number(amount) || 0;
  const chargeAmount =
    Math.round(((enteredAmount * transferCharge) / 100) * 100) / 100;
  const totalDebit = Math.round((enteredAmount + chargeAmount) * 100) / 100;

  const fetchData = async () => {
    try {
      const { data } = await Axios.get(
        api + `/api/users/point/${userInfo._id}`,
        {
          headers: { authorization: `Bearer ${userInfo.token}` },
        },
      );
      if (data.moneyPolicy) setPolicy(data.moneyPolicy);
      setMinimumTransfer(data.moneyPolicy?.minimumTransfer ?? data.minimumTransfer);
      setRegisterPoint(data.point);
    } catch (error) {
      // window.alert(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userInfo]);

  const otpGate = useOtpGate({
    path: "transfer",
    token: userInfo.token,
    onSuccess: (data) => {
      toast.info(data?.message || "Transfer completed.");
      fetchData();
      setAmount(0);
      setPassword("");
      setConvertTo("0");
    },
    onError: (error) => toast.error(getError(error)),
  });

  const convert = async () => {
    // The server enforces this too; checking here saves the member filling in a
    // form that was never going to be accepted.
    if (!policy.transferEnabled) {
      toast.error(
        policy.transferDisabledMessage ||
          "Point transfers are temporarily turned off. Please try again later.",
      );
      return;
    }
    if (convertFrom === "0" || convertFrom === 0) {
      toast.error("Please choose what to transfer from.");
      return;
    }
    if (!convertTo || convertTo === "0" || convertTo === 0) {
      toast.error("Please enter the recipient's username.");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Please enter an amount greater than 0.");
      return;
    }
    if (minimumTransfer && Number(amount) < Number(minimumTransfer)) {
      toast.error("Minimum transfer amount is " + minimumTransfer + ".");
      return;
    }
    if (
      Number(policy.maximumTransfer) > 0 &&
      Number(amount) > Number(policy.maximumTransfer)
    ) {
      toast.error(
        "Maximum transfer amount is " + policy.maximumTransfer + ".",
      );
      return;
    }
    if (!password || password.length < 4) {
      toast.error("Please enter your password (at least 4 characters).");
      return;
    }
    if (convertFrom === convertTo) {
      toast.error("You cannot transfer to yourself.");
      return;
    }
    // The charge comes out of the sender's balance on top of the amount, so the
    // balance has to cover both.
    if (convertFrom === "point" && totalDebit > RegisterPoint) {
      toast.error(
        "Not enough Register Balance. You have " +
          RegisterPoint +
          ". You need " +
          totalDebit +
          ".",
      );
      return;
    }
    // Runs through the OTP gate; identical request when the gate is off.
    setloading(1);
    await otpGate.start({
      from: convertFrom,
      senderUsername: userInfo.username,
      to: convertTo,
      amount: amount,
      password: password,
    });
    setloading(0);
  };
  // The looked-up recipient, shown as a card so the sender can see exactly who
  // they are paying before they confirm.
  const [recipient, setRecipient] = useState(null);
  const [userLoading, setUserLoading] = useState(0);
  const checkUsername = async (raw) => {
    const username = String(raw || "").trim();
    setRecipient(null);
    setConvertTo(0);
    if (!username) return;
    try {
      setUserLoading(1);
      const { data } = await Axios.post(
        api + `/api/users/checkusernamefortransfer/`,
        {
          value: username,
        },
        {
          headers: { authorization: `Bearer ${userInfo.token}` },
        },
      );
      setUserLoading(0);
      setRecipient({
        name: data.name || "",
        username: data.username || username,
        profilePhoto: data.profilePhoto || "",
      });
      setConvertTo(data.username || username);
    } catch (error) {
      setUserLoading(0);
      toast.error(
        "We could not find this username. Please check and try again.",
      );
    }
  };
  function gotoHome() {
    navigate("/transferandhistory");
  }
  return (
    <div>
      <Helmet>
        <title>Transfer </title>
      </Helmet>
      <OtpGateModal
        gate={otpGate}
        title="Confirm this transfer"
        actionLabel="Confirm transfer"
      />
      <Container className="no-gutter px-3" style={{ maxWidth: 560 }}>
        <div className="card">
          <Row>
            <Col style={{ textAlign: "center" }}>
              <img
                src="https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png"
                style={{ width: "min(15rem, 60vw)", margin: "1.5rem auto" }}
                alt="ClickCart Logo"
              />
            </Col>
          </Row>
          <div
            className="card-header"
            onClick={() => {
              gotoHome();
            }}
          >
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i> {"  "}{" "}
            Transfer
          </div>

          <div className="card-body" style={{ width: "100%" }}>
            {!policy.transferEnabled && (
              <Alert key="transfer-off" variant="danger">
                {policy.transferDisabledMessage ||
                  "Point transfers are temporarily turned off. Please try again later."}
              </Alert>
            )}
            {policy.transferEnabled &&
              (Number(policy.minimumTransfer) > 0 ||
                Number(policy.maximumTransfer) > 0 ||
                transferCharge > 0) && (
                <Alert key="transfer-limits" variant="info" className="py-2">
                  <span className="small">
                    {Number(policy.minimumTransfer) > 0 &&
                      `Minimum: ${policy.minimumTransfer}. `}
                    {Number(policy.maximumTransfer) > 0 &&
                      `Maximum: ${policy.maximumTransfer}. `}
                    {transferCharge > 0 &&
                      `Charge: ${transferCharge}% of the amount, paid by you on top of it.`}
                  </span>
                </Alert>
              )}
            <span>
              {"Register Point: " + RegisterPoint}
            </span>
            <div className="input-group mb-3">
              <div className="input-group-text">
                <i className="fa fa-level-down" aria-hidden="true"></i>
              </div>
              <select className="form-control " value="point" disabled>
                <option value="point">Register Point</option>
              </select>{" "}
            </div>
            <div className="input-group mb-3">
              <div className="input-group-text">
                <i className="fa fa-user" aria-hidden="true"></i>
              </div>
              <input
                type="text"
                className="form-control"
                required
                placeholder="Username"
                onBlur={(e) => checkUsername(e.target.value)}
              />
            </div>
            {userLoading ? (
              <div className="d-flex align-items-center gap-2 mb-3 text-muted small">
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                Looking up member…
              </div>
            ) : recipient ? (
              <div
                className="d-flex align-items-center gap-3 mb-3 p-3 rounded-3 border"
                style={{ background: "#f4fbf7", borderColor: "#bfe5d0" }}
              >
                {recipient.profilePhoto ? (
                  <img
                    src={recipient.profilePhoto}
                    alt={recipient.name || recipient.username}
                    className="rounded-circle"
                    style={{ width: 56, height: 56, objectFit: "cover", flex: "none" }}
                  />
                ) : (
                  <div
                    className="rounded-circle d-grid"
                    style={{
                      width: 56,
                      height: 56,
                      placeItems: "center",
                      background: "#0f7a55",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "1.3rem",
                      flex: "none",
                    }}
                  >
                    {(recipient.name || recipient.username).charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-truncate">
                    {recipient.name || recipient.username}
                  </div>
                  <div className="text-muted small">@{recipient.username}</div>
                </div>
                <i
                  className="fa fa-check-circle ms-auto"
                  aria-hidden="true"
                  style={{ color: "#0f7a55", fontSize: "1.4rem" }}
                ></i>
              </div>
            ) : null}
            <div className="input-group mb-3">
              <div className="input-group-text">
                <i className="fa fa-dollar" aria-hidden="true"></i>
              </div>
              <input
                className="form-control"
                type="number"
                placeholder="Amount"
                required
                disabled={!policy.transferEnabled}
                onChange={(e) => setAmount(e.target.value)}
              ></input>
            </div>
            {transferCharge > 0 && enteredAmount > 0 && (
              <div className="mb-3">
                <span className="small">
                  Charge: {chargeAmount} | Total deducted from you: {totalDebit}{" "}
                  | Recipient receives: {enteredAmount}
                </span>
              </div>
            )}
            <div className="input-group mb-3">
              <div className="input-group-text">
                <i className="fa fa-asterisk" aria-hidden="true"></i>
              </div>
              <input
                type="password"
                className="form-control"
                placeholder="Password"
                required
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button
              variant="outline-success w-100"
              className={loading || !policy.transferEnabled ? "disabled" : ""}
              disabled={Boolean(loading) || !policy.transferEnabled}
              onClick={convert}
            >
              {loading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                ""
              )}{" "}
              Submit
            </Button>
            <SecurityBadges variant="inline" />
          </div>

          <div style={{ height: "2rem" }}> {""}</div>
        </div>
      </Container>
    </div>
  );
}
